import { responseInCut } from '@/components/quick-study-results/quickStudyPure'
import { TwinResponse } from '@/components/quick-study-results/quickStudyTypes'

export interface HighlightMagazineStripPersonasOptions {
  featuredQuestionName?: string
  featuredItemId?: string
  activeCutIds?: Array<string>
}

function hasAnswerValue(value: unknown): boolean {
  if (value == null) {
    return false
  }
  if (typeof value === 'string') {
    return value.trim().length > 0
  }
  if (typeof value === 'number') {
    return Number.isFinite(value)
  }
  if (typeof value === 'boolean') {
    return true
  }
  if (Array.isArray(value)) {
    return value.length > 0
  }
  if (typeof value === 'object') {
    return Object.keys(value).length > 0
  }
  return false
}

/**
 * Candidate `survey_data` keys for "the featured question", in priority order. Exported
 * (not just used locally) because `resolveMagazineBandSource`'s `featuredQuestionName`
 * is not always the real SurveyJS field name: when a strip's own `questionId` metadata is
 * unavailable, the caller falls back to a per-exhibit identifier (a UUID) that never
 * matches any `survey_data` key — `'qs_main'` is Quick Study's canonical open-end/main
 * question key and is always tried first regardless, which is why highlighting here
 * already works on documents `buildRowQuotes.ts` initially did not (live QA finding,
 * thread 9a20d99e: `featuredQuestionName` resolved to a bare UUID with 50/50 answered
 * rows sitting under `survey_data.qs_main`).
 */
export function featuredQuestionKeys(featuredQuestionName?: string): Array<string> {
  const keys = ['qs_main']
  const name = (featuredQuestionName || '').trim()
  if (name && !keys.includes(name)) {
    keys.push(name)
  }
  if (name && (/why|comment|open/i.test(name) || name === 'qs_main_why')) {
    if (!keys.includes('qs_main_why')) {
      keys.push('qs_main_why')
    }
  }
  return keys
}

function hasAnsweredFeaturedQuestion(
  row: TwinResponse,
  featuredQuestionName?: string,
): boolean {
  const data = row.survey_data || {}
  return featuredQuestionKeys(featuredQuestionName).some((key) => hasAnswerValue(data[key]))
}

/** Shared with `resolveMagazineBandSource`'s KPI item scoping (D4) — the only piece of
 *  this module that's name-agnostic-safe to reuse there. Never import the `qs_main` /
 *  `/why|comment|open/i` heuristics above into that module. */
export function responseItemId(row: TwinResponse): string {
  if (typeof row.item_id === 'string' && row.item_id.trim()) {
    return row.item_id.trim()
  }
  const raw = row.attributes?.item_id
  return typeof raw === 'string' ? raw.trim() : ''
}

function personaIdFromRow(row: TwinResponse): string {
  return typeof row.persona_id === 'string' ? row.persona_id.trim() : ''
}

/**
 * Respondents who answered the featured question and/or belong to the
 * active cut. `undefined` when no filter is active so the mosaic stays
 * full-color. An empty array dims every tile.
 */
export function highlightMagazineStripPersonas(
  responses: Array<TwinResponse>,
  options: HighlightMagazineStripPersonasOptions = {},
): Array<string> | undefined {
  const featuredQuestionName = (options.featuredQuestionName || '').trim()
  const featuredItemId = (options.featuredItemId || '').trim()
  const activeCutIds = (options.activeCutIds || []).map((id) => id.trim()).filter(Boolean)
  const hasQuestionFilter = featuredQuestionName.length > 0
  const hasItemFilter = featuredItemId.length > 0
  const hasCutFilter = activeCutIds.length > 0

  if (!hasQuestionFilter && !hasItemFilter && !hasCutFilter) {
    return undefined
  }

  const ids: Array<string> = []
  const seen = new Set<string>()

  responses.forEach((row) => {
    const personaId = personaIdFromRow(row)
    if (!personaId || seen.has(personaId)) {
      return
    }
    if (hasQuestionFilter && !hasAnsweredFeaturedQuestion(row, featuredQuestionName)) {
      return
    }
    if (hasItemFilter && responseItemId(row) !== featuredItemId) {
      return
    }
    if (hasCutFilter && !activeCutIds.some((cutId) => responseInCut(row, cutId))) {
      return
    }
    seen.add(personaId)
    ids.push(personaId)
  })

  return ids
}
